/**
 * The chips a Manifest Review board draws.
 *
 *   WordChip  a PA status word (the Patient Advocate board's words: TWL,
 *             C-TWL, REPLACEMENT, DNQ, ...) FILLED with its Monday colour,
 *             as Monday draws it (FilledStatusChip). The colours are the
 *             server's one governed list (advocateNativeService STATUS_LEGEND,
 *             served as scope.status_legend), provided once by the shell.
 *             A word the list does not hold is drawn plainly, never given
 *             somebody else's colour.
 *   ToneChip  eMerge's own tones (ok / attn / stop / quiet) for eMerge facts
 *             that are not Monday words. Unchanged: eMerge tones stay eMerge's.
 */
import React, { createContext, useContext } from 'react';
import FilledStatusChip from '../../../components/status/FilledStatusChip';
import type { StatusLegendEntry } from '../../advocate/advocateApi';

export const StatusPaletteContext = createContext<StatusLegendEntry[]>([]);

export function useWordColour(word: string | null | undefined): StatusLegendEntry | null {
  const legend = useContext(StatusPaletteContext);
  const k = String(word || '').trim().toUpperCase().replace(/\s+/g, ' ');
  if (!k) return null;
  return legend.find((l) => l.word === k) || null;
}

export function WordChip({ word, label, title }: { word: string | null | undefined; label?: React.ReactNode; title?: string }) {
  const hit = useWordColour(word);
  if (!word) return null;
  if (!hit) return <span className="mf-chip mf-chip--plain" title={title || 'No Monday colour is held for this word.'}>{label ?? word}</span>;
  return <FilledStatusChip word={hit.word} hex={hit.color} label={label ?? hit.word} title={title || hit.label} size="sm" className="mf-chip mf-chip--filled" />;
}

export type Tone = 'ok' | 'attn' | 'stop' | 'quiet';

export function ToneChip({ tone, children, title }: { tone: Tone; children: React.ReactNode; title?: string }) {
  return <span className={`mf-chip mf-chip--${tone}`} title={title}>{children}</span>;
}
